import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { apiConfig } from 'src/app/api.config';
import { environment } from 'src/environments/environment';
import { IArrayResponse } from '../common/models/general.model';
import {
  Challenge,
  DiagnosisChallenge,
  SubChallengePayload,
} from '../common/models/master-data/challenge';
import { GateAction, Reference } from '../common/models/master-data/metadata';
import {
  DiagnosisRequiredAction,
  DiagnosisRequiredActionUpdateDto,
} from '../common/models/workflow/diagnosis-metadata';
import {
  DiagnosisSession,
  InitialSession,
} from '../common/models/workflow/diagnosis-session';
import { VDRJob } from '../common/models/workflow/vdr-job';

@Injectable({
  providedIn: 'root',
})
export class DiagnosisSessionService {
  private http = inject(HttpClient);
  baseUrl = environment.api;

  initializeSession(initialSession: InitialSession) {
    return this.http.post<DiagnosisSession>(
      this.baseUrl + apiConfig.diagnosisSession.initialize,
      initialSession,
    );
  }

  restartSession(sessionId: string): Observable<DiagnosisSession> {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.restart.replace(':sessionId', sessionId),
    );
  }

  completeSession(sessionId: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.complete.replace(':sessionId', sessionId),
    );
  }

  resetSession(sessionId: string, pslCode: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.reset
          .replace(':sessionId', sessionId)
          .replace(':pslCode', pslCode),
    );
  }

  completeVDRSession(sessionId: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.completeVDRSession.replace(
          ':sessionId',
          sessionId,
        ),
    );
  }

  completeOneOffSession(sessionId: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.completeOneOffSession.replace(
          ':sessionId',
          sessionId,
        ),
    );
  }

  approveSession(sessionId: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.approve.replace(':sessionId', sessionId),
    );
  }

  back(sessionId: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.back.replace(':sessionId', sessionId),
    );
  }

  getDiagnosisSessionById(sessionId: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.getDiagnosisSessionById.replace(
          ':sessionId',
          sessionId,
        ),
    );
  }

  getSessionInfoById(sessionId: string) {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.getSessionInfoById.replace(
          ':sessionId',
          sessionId,
        ),
    );
  }

  getDiagnosisSessionByExternalId(
    externalId: string,
  ): Observable<DiagnosisSession> {
    return this.http.get<DiagnosisSession>(
      this.baseUrl +
        apiConfig.diagnosisSession.getDiagnosisSessionByExternalId.replace(
          ':externalId',
          externalId,
        ),
    );
  }

  getByCurrentUser() {
    return this.http.get<DiagnosisSession>(
      this.baseUrl + apiConfig.diagnosisSession.getByCurrentUser,
    );
  }

  getSessionChallenges(sessionId: string) {
    return this.http
      .get<
        IArrayResponse<DiagnosisChallenge>
      >(this.baseUrl + apiConfig.diagnosisSession.getSessionChallenges.replace(':sessionId', sessionId))
      .pipe(map((res) => res.results));
  }

  addPairChallenges(payload: SubChallengePayload) {
    return this.http.post<IArrayResponse<Challenge>>(
      this.baseUrl + apiConfig.diagnosisChallenge.add,
      payload,
    );
  }

  removePairChallenge(payload: SubChallengePayload) {
    return this.http.post<IArrayResponse<Challenge>>(
      this.baseUrl + apiConfig.diagnosisChallenge.remove,
      payload,
    );
  }

  getSuggestedRequiredActions(sessionId: string) {
    return this.http
      .get<
        IArrayResponse<GateAction>
      >(this.baseUrl + apiConfig.diagnosisMetadata.getSuggestedRequiredActions.replace(':sessionId', sessionId))
      .pipe(map((res) => res.results));
  }

  attachedSuggestedRequiredActions(sessionId: string) {
    return this.http
      .get<
        IArrayResponse<DiagnosisRequiredAction>
      >(this.baseUrl + apiConfig.diagnosisMetadata.attachedSuggestedRequiredActions.replace(':sessionId', sessionId))
      .pipe(map((res) => res.results));
  }

  confirmRequiredAction(requiredAction: DiagnosisRequiredActionUpdateDto) {
    return this.http.post<DiagnosisRequiredAction>(
      this.baseUrl + apiConfig.diagnosisMetadata.confirmRequiredAction,
      requiredAction,
    );
  }

  getAttachedRequiredActions(sessionId: string) {
    return this.http
      .get<
        IArrayResponse<DiagnosisRequiredAction>
      >(this.baseUrl + apiConfig.diagnosisMetadata.getAttachedRequiredActions.replace(':sessionId', sessionId))
      .pipe(map((res) => res.results));
  }

  getDiagnosisReferences(sessionId: string) {
    return this.http
      .get<
        IArrayResponse<Reference>
      >(this.baseUrl + apiConfig.diagnosisMetadata.getDiagnosisReferences.replace(':sessionId', sessionId))
      .pipe(map((res) => res.results));
  }

  getRelatedVDRJob(sessionId: string) {
    return this.http.get<VDRJob>(
      this.baseUrl +
        apiConfig.diagnosisSession.getRelatedVDRJob.replace(
          ':sessionId',
          sessionId,
        ),
    );
  }
}
